/**
 * Mulberry32 deterministic 32-bit PRNG.
 * Produces reproducible pseudo-random numbers from a string or numeric seed.
 */
export class PRNG {
  private state: number;

  constructor(seed: string | number) {
    this.state = typeof seed === 'number' ? seed >>> 0 : PRNG.hashString(seed);
  }

  /**
   * Generates a deterministic hash from a string
   */
  public static hashString(str: string): number {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
      h = Math.imul(h ^ str.charCodeAt(i), 16777619) >>> 0;
    }
    return h;
  }

  /**
   * Generates a random alphanumeric seed code (e.g., "AURA-7K9P")
   */
  public static generateSeedCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'AURA-';
    for (let i = 0; i < 4; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  /**
   * Returns a float between 0 (inclusive) and 1 (exclusive)
   */
  public next(): number {
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /**
   * Returns a float between min and max
   */
  public nextRange(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /**
   * Returns an integer between min and max (inclusive)
   */
  public nextInt(min: number, max: number): number {
    return Math.floor(this.nextRange(min, max + 1));
  }
}
